<template>
  <BlokkliProvider
    v-if="event && series"
    v-slot="{ isEditing }"
    v-bind="series.blokkliProps"
  >
    <PageTitleWithImage
      :image="series?.image"
      :title="series?.label"
      :tagline="series.eventType?.name"
    >
      <template v-if="dates" #date>
        <FieldItemTypeDateRange v-bind="dates" />
      </template>

      <template #after>
        <ul v-if="series.targetGroup?.length" class="text-xl">
          <li v-for="item in series.targetGroup" :key="item.id">
            <span>{{ item.name }}</span>
          </li>
        </ul>

        <OrderedList
          v-if="accessibilityItems"
          v-slot="{ item }"
          :list="accessibilityItems"
          class="flex gap-2"
        >
          <MediaIcon
            v-bind="item.icon"
            class="size-9 md:size-12"
            :title="item.name"
          />
          <span class="sr-only">{{ item.name }}</span>
        </OrderedList>

        <InPageNavigation :anchors="anchors" class="mt-6" />
      </template>
    </PageTitleWithImage>

    <ContainerGrid
      v-if="hasAbout"
      id="about"
      class="container pb-6 mt-6 lg:mt-10"
    >
      <div class="col-span-full lg:col-span-8">
        <div
          v-if="event.addendum"
          v-blokkli-editable:body
          class="ck-content hyphens-auto lg:hyphens-none pb-4 font-bold"
          v-html="event.addendum"
        />
        <p v-if="series?.lead">{{ series?.lead }}</p>
      </div>
    </ContainerGrid>

    <div>
      <BlokkliField
        :list="series?.paragraphs"
        name="field_paragraphs"
        class="page-paragraphs"
        :allowed-fragments="['event_exhibition']"
      />
    </div>
    <Locations
      v-if="
        series?.locations &&
        series?.locations.length &&
        registrationEnabled &&
        !eventStartedInThePast
      "
      :locations="series.locations?.map((l) => l.location)"
      :sticky="!isEditing"
      show-link
    >
      <DrupalLink
        v-if="registrationOpenAndAvailableSeats"
        :to="registerUrl"
        class="button"
      >
        {{ $texts('event.register', 'Anmelden') }}
      </DrupalLink>
      <DrupalLink
        v-else-if="noAvailableSeats"
        class="button is-disabled"
        disabled
      >
        {{ $texts('event.fully_booked', 'Ausgebucht') }}
      </DrupalLink>
      <div v-else class="button is-disabled" disabled>
        {{ $texts('event.register', 'Anmelden') }}
      </div>
    </Locations>
    <Locations
      v-else-if="
        series?.locations && series?.locations.length && !eventStartedInThePast
      "
      :locations="series.locations?.map((l) => l.location)"
      :sticky="!isEditing"
      show-link
    />
  </BlokkliProvider>
</template>

<script lang="ts" setup>
import { notNullish } from '@vueuse/core'
import type {
  EventinstanceDefaultFragment,
  EventseriesDefaultFragment,
} from '#graphql-operations'

const props = defineProps<{
  event: EventinstanceDefaultFragment
}>()

const exhibitions = computed(() => series.value?.exhibition)
provide('event_exhibition', exhibitions)

const hasExhibitions = computed(() => !!exhibitions.value?.length)

const { $texts } = useEasyTexts()

const series = computed<EventseriesDefaultFragment | undefined>(
  () => props.event.series,
)

const hasAbout = computed(() => !!(props.event.addendum || series.value?.lead))

const anchors = defineAnchorLinks((v) => {
  v.add(
    $texts('event.information_anchor', 'Über die Veranstaltung'),
    'about',
    hasAbout,
  ).addParagraphs(series.value?.paragraphs, {
    event_exhibition: hasExhibitions,
  })
})

const dates = computed<EventinstanceDefaultFragment['dates'] | undefined>(
  () => props.event.dates,
)

const datePrint = computed(() => props.event.dates?.datePrint || '')

const { availableSeats, registrationEnabled, registerUrl, registrationOpen } =
  useEventRegistration(props.event.registrationState)

const registrationOpenAndAvailableSeats = computed(
  () => registrationOpen.value && availableSeats.value > 0,
)

const noAvailableSeats = computed(() => availableSeats.value <= 0)

const registrationEnabledAndAvailableSeats = computed(
  () => registrationEnabled.value && availableSeats.value > 0,
)

const accessibilityItems = computed(() =>
  series.value?.accessibility
    ?.map((v) => {
      if (v.icon && v.name) {
        return v
      }

      return null
    })
    .filter(notNullish),
)

const eventStartedInThePast = computed(() => {
  const timestampStart = props.event?.dates?.timestampStart

  return timestampStart ? parseInt(timestampStart) < Date.now() / 1000 : false
})

defineVisitorTableData((data) => {
  data
    .add(
      'date',
      {
        ...dates.value,
        datePrint: datePrint?.value,
      },
      datePrint,
    )

    .add(
      'address',
      series.value?.locationAddresses
        ?.map((v) => ({
          ...v.location.address,
          ...v.location.addressLink,
        }))
        .filter(notNullish),
    )
    .add('price', series.value?.price)
    .add(
      'availability_count',
      availableSeats.value,
      registrationEnabledAndAvailableSeats,
    )
    .add('accessibility', series.value?.accessibility)
    .add(
      'languages',
      series.value?.languages?.map((language) => language.label).join(', '),
    )
    .add('info', series.value?.visitorInformation)
})
</script>
